import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Plane, Info, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LEDGERS } from "@/constants/testIds";

/**
 * Travel-cash ledger for one employee. Admin only. Each entry is a NET
 * cash amount handed pre-pay-run; the gross-up is calculated at pay-run
 * time. The "indicative tax" button hits /preview-tax which returns a
 * clearly-labelled estimate — never stored as final.
 */
export default function TravelLedgerSection({ employee, onChanged }) {
    const entries = (employee?.travel_ledger || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    const [date, setDate] = useState("");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    // Reset preview when amount/date change materially
    useEffect(() => { setPreview(null); }, [amount, date]);

    const add = async () => {
        if (!date || !amount) return;
        setBusy(true);
        try {
            await api.post(`/employees/${employee.id}/travel-ledger`, {
                date, amount_kina: Number(amount), note,
            });
            setDate(""); setAmount(""); setNote(""); setPreview(null);
            onChanged?.();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Add failed");
        } finally { setBusy(false); }
    };

    const previewTax = async () => {
        if (!date || !amount) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/employees/${employee.id}/travel-ledger/preview-tax`, {
                date, amount_kina: Number(amount), note,
            });
            setPreview(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this travel entry?")) return;
        try {
            await api.delete(`/employees/${employee.id}/travel-ledger/${id}`);
            onChanged?.();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    return (
        <section data-testid={LEDGERS.travelSection} className="mt-6 border border-neutral-200 rounded-[3px] p-4 bg-white">
            <div className="flex items-center gap-2 mb-3">
                <Plane size={14} className="text-[color:var(--kp-red)]" />
                <div className="text-[11px] uppercase tracking-[0.18em] font-display font-semibold text-[color:var(--kp-charcoal)]">
                    Travel ledger
                </div>
                <div className="text-[10px] text-neutral-500 ml-2">
                    Cash NET handed pre-pay-run · grossed up at pay time
                </div>
            </div>

            <div className="grid grid-cols-[130px_140px_1fr_auto] gap-2 items-start mb-2">
                <input data-testid={LEDGERS.travelAddDate} type="date" value={date}
                       onChange={(e) => setDate(e.target.value)}
                       className="kp-focus border border-neutral-300 px-2 py-1.5 text-[12px] font-mono-money rounded-[2px]" />
                <input data-testid={LEDGERS.travelAddAmount} type="number" step="0.01" placeholder="Kina"
                       value={amount} onChange={(e) => setAmount(e.target.value)}
                       className="kp-focus border border-neutral-300 px-2 py-1.5 text-[12px] font-mono-money rounded-[2px]" />
                <input data-testid={LEDGERS.travelAddNote} placeholder="Note" value={note}
                       onChange={(e) => setNote(e.target.value)}
                       className="kp-focus border border-neutral-300 px-2 py-1.5 text-[12px] rounded-[2px]" />
                <div className="flex gap-1">
                    <button data-testid={LEDGERS.travelPreviewBtn} onClick={previewTax}
                            disabled={busy || !date || !amount}
                            className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-display border border-neutral-300 text-neutral-700 rounded-[2px] disabled:opacity-40 hover:border-neutral-500">
                        Estimate tax
                    </button>
                    <button data-testid={LEDGERS.travelAddBtn} onClick={add}
                            disabled={busy || !date || !amount}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-display font-semibold text-white rounded-[2px] disabled:opacity-40"
                            style={{ background: "var(--kp-red)" }}>
                        <Plus size={12} /> Add
                    </button>
                </div>
            </div>

            {preview && (
                <div className={`rounded-[2px] p-3 mb-3 text-[12px] border ${preview.near_boundary ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-blue-50 border-blue-200 text-blue-900"}`}>
                    <div className="flex items-center gap-2 font-display font-semibold uppercase tracking-[0.14em] text-[10px] mb-1">
                        <Info size={11} /> Indicative estimate (not final)
                    </div>
                    <div className="font-mono-money">
                        Gross-up ≈ <strong>K{Number(preview.gross_up_kina).toFixed(2)}</strong>
                        {" · "}tax cost ≈ K{Number(preview.marginal_tax_kina).toFixed(2)}
                        {" · "}net = K{Number(preview.net_handed_kina).toFixed(2)}
                    </div>
                    {preview.near_boundary && (
                        <div className="mt-2 flex items-start gap-1">
                            <AlertTriangle size={11} className="mt-0.5" />
                            <span>{preview.near_boundary_note}</span>
                        </div>
                    )}
                    <div className="mt-1 text-[10px] opacity-80">
                        Real gross-up is computed at pay-run time using this fortnight's actual earnings.
                    </div>
                </div>
            )}

            {entries.length === 0 ? (
                <div className="text-[11px] text-neutral-400 italic py-4">No travel entries yet.</div>
            ) : (
                <table className="w-full text-[12px]">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 font-display">
                            <th className="text-left py-1 px-2 font-semibold">Date</th>
                            <th className="text-right py-1 px-2 font-semibold">Amount</th>
                            <th className="text-left py-1 px-2 font-semibold">Note</th>
                            <th className="text-left py-1 px-2 font-semibold">Recorded by</th>
                            <th className="w-8"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((e) => (
                            <tr key={e.id} data-testid={LEDGERS.travelRow(e.id)} className="border-t border-neutral-100">
                                <td className="py-1.5 px-2 font-mono-money">{e.date}</td>
                                <td className="py-1.5 px-2 text-right font-mono-money">K{Number(e.amount_kina).toFixed(2)}</td>
                                <td className="py-1.5 px-2 text-neutral-600">{e.note || "—"}</td>
                                <td className="py-1.5 px-2 text-[10px] text-neutral-500">{e.recorded_by || "—"}</td>
                                <td className="py-1.5 px-2 text-right">
                                    <button data-testid={LEDGERS.travelDeleteBtn(e.id)}
                                            onClick={() => remove(e.id)}
                                            className="text-neutral-400 hover:text-[color:var(--kp-red)]">
                                        <Trash2 size={13} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}
